import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { gaEvents, getGaClientId } from '@/hooks/useGoogleAnalytics';
import { forwardLead } from '@/lib/forwardLead';

// Sanitize input to prevent XSS and SQL injection
const sanitizeInput = (input: string, maxLength: number = 500): string => {
  return input
    .trim()
    .slice(0, maxLength)
    .replace(/[<>]/g, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+=/gi, '');
};

// Validate phone number format
const isValidPhone = (phone: string): boolean => {
  if (!phone) return true;
  const phoneRegex = /^[\d\s\-\+\(\)\.]{7,30}$/;
  return phoneRegex.test(phone);
};

interface LoanOfficerInfo {
  name?: string | null;
  company?: string | null;
  email?: string | null;
  phone?: string | null;
  license?: string | null;
}

interface OfferLeadFormProps {
  siteId: string;
  requestType: string;
  pagePath?: string;
  submitButtonText?: string;
  className?: string;
  siteDomain?: string;
  loanOfficer?: LoanOfficerInfo | null;
}

export function OfferLeadForm({
  siteId,
  requestType,
  pagePath = '',
  submitButtonText = 'Submit',
  className = '',
  siteDomain = '',
  loanOfficer = null,
}: OfferLeadFormProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [category, setCategory] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [formStarted, setFormStarted] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const { toast } = useToast();

  const categoryOptions = ['Buyer', 'Seller', 'Broker', 'Mortgage Officer', 'Other'];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!siteId) {
      toast({
        title: 'Error',
        description: 'Site not configured. Please try again later.',
        variant: 'destructive',
      });
      return;
    }

    // Honeypot check
    if (honeypot) {
      setSubmitted(true);
      return;
    }

    // Sanitize inputs
    const sanitizedName = sanitizeInput(name, 100);
    const sanitizedEmail = email.trim().toLowerCase().slice(0, 255);
    const sanitizedPhone = sanitizeInput(phone, 30);
    const sanitizedAddress = sanitizeInput(address, 255);

    if (!sanitizedName || !sanitizedEmail) {
      toast({
        title: 'Required fields',
        description: 'Please fill in your name and email.',
        variant: 'destructive',
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(sanitizedEmail)) {
      toast({
        title: 'Invalid email',
        description: 'Please enter a valid email address.',
        variant: 'destructive',
      });
      return;
    }

    // Phone validation
    if (sanitizedPhone && !isValidPhone(sanitizedPhone)) {
      toast({
        title: 'Invalid phone',
        description: 'Please enter a valid phone number.',
        variant: 'destructive',
      });
      return;
    }

    // Category validation
    if (category.length === 0) {
      toast({
        title: 'Category required',
        description: 'Please select at least one category (I am a...).',
        variant: 'destructive',
      });
      return;
    }

    // Consent validation
    if (!consentGiven) {
      toast({
        title: 'Consent required',
        description: 'Please agree to receive communications before submitting.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const gaClientId = getGaClientId();

      // Include address in message if provided (for market value reports)
      const messageContent = sanitizedAddress 
        ? `Property Address: ${sanitizedAddress}`
        : null;

      const { error } = await supabase.from('leads').insert({
        site_id: siteId,
        name: sanitizedName,
        email: sanitizedEmail,
        phone: sanitizedPhone || null,
        message: messageContent,
        page_path: pagePath?.slice(0, 255) || null,
        ga_client_id: gaClientId,
        request_type: requestType,
        site_domain: siteDomain || null,
        category: category,
        loan_officer_name: loanOfficer?.name || null,
        loan_officer_company: loanOfficer?.company || null,
        loan_officer_email: loanOfficer?.email || null,
        loan_officer_phone: loanOfficer?.phone || null,
        loan_officer_license: loanOfficer?.license || null,
      });

      if (error) throw error;

      // Forward lead to external API (non-blocking)
      forwardLead({
        name: sanitizedName,
        email: sanitizedEmail,
        phone: sanitizedPhone || null,
        message: messageContent,
        site_id: siteId,
        page_path: pagePath || null,
        request_type: requestType,
        site_domain: siteDomain || null,
        category: category,
        loan_officer: loanOfficer,
      });

      setSubmitted(true);

      // Track lead generation event with request type
      gaEvents.generateLead(requestType, {
        page_path: pagePath,
        has_phone: !!sanitizedPhone,
        has_address: !!sanitizedAddress,
      });

    } catch (error) {
      console.error('Error submitting lead:', error);
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <div className="mb-4">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        <h4 className="text-xl font-semibold mb-2">Thank You!</h4>
        <p className="text-muted-foreground">
          {requestType === 'market_value_report' 
            ? "We're preparing your market value report. You'll receive it shortly!"
            : "Your community guide is on its way! Check your email soon."
          }
        </p>
      </div>
    );
  }

  const showAddressField = requestType === 'market_value_report';

  return (
    <form onSubmit={handleSubmit} className={`space-y-4 ${className}`}>
      {/* Honeypot field */}
      <div className="hidden" aria-hidden="true">
        <Input
          type="text"
          name="website"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="offer-name">Name *</Label>
        <Input
          id="offer-name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (!formStarted) {
              setFormStarted(true);
              gaEvents.formStart(requestType, pagePath);
            }
          }}
          placeholder="Your name"
          required
          maxLength={100}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="offer-email">Email *</Label>
        <Input
          id="offer-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          required
          maxLength={255}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="offer-phone">Phone</Label>
        <Input
          id="offer-phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="(555) 123-4567"
          maxLength={30}
        />
      </div>

      {showAddressField && (
        <div className="space-y-2">
          <Label htmlFor="offer-address">Property Address</Label>
          <Input
            id="offer-address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="123 Main St, City, State"
            maxLength={255}
          />
        </div>
      )}

      <div className="space-y-2">
        <Label>I am a... *</Label>
        <div className="flex flex-wrap gap-2">
          {categoryOptions.map((option) => (
            <label
              key={option}
              className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                category.includes(option)
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background border-input hover:bg-muted'
              }`}
            >
              <Checkbox
                checked={category.includes(option)}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setCategory([...category, option]);
                  } else {
                    setCategory(category.filter((c) => c !== option));
                  }
                }}
                className="sr-only"
              />
              <span className="text-sm">{option}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex items-start space-x-3">
        <Checkbox
          id="offer-consent"
          checked={consentGiven}
          onCheckedChange={(checked) => setConsentGiven(checked === true)}
        />
        <label
          htmlFor="offer-consent"
          className="text-xs leading-relaxed cursor-pointer text-muted-foreground"
        >
          By submitting this form, you consent to be contacted by email, phone, and text, and to the use of your information for advertising and personalized advertising. Message and data rates may apply. Reply STOP to opt out.
        </label>
      </div>

      <Button
        type="submit"
        size="lg"
        variant="accent"
        className="w-full"
        disabled={loading}
      >
        {loading ? 'Submitting...' : submitButtonText}
      </Button>

      <p className="text-xs text-center text-muted-foreground">
        Your information is secure and will never be shared.
      </p>
    </form>
  );
}
